import * as React from 'react'
import { CaretDown, Check } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Button } from './button'

/**
 * Shared chrome for *filter popovers* — the bordered pill trigger and the
 * Reset / Cancel / Apply footer that the `RangeFilter` and `MultiSelectFilter`
 * both wear. These sit on top of `Popover` (not `DropdownMenu`): a filter panel
 * holds focusable inputs/checkboxes, which a menu's typeahead would fight.
 *
 * Lifecycle (`useFilterDraft`): edits are staged in a *draft* and only committed
 * on Apply. Opening reseeds the draft from the committed value; Cancel discards
 * it; Reset clears it (without closing). This is the app's pending-filter model.
 */

/** Resting + open/active states for the pill trigger (size-agnostic). */
const triggerBase =
  // Border colors carry `!` so Bootstrap's global border utilities can't shadow
  // them to a different gray in the app shell (matches Input/InputGroup, which
  // already use `!border-*`). No-op in Storybook; fixes the in-app stroke.
  'group relative inline-flex w-full items-center justify-between border bg-fbui-base-white font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand disabled:cursor-not-allowed disabled:!border-fbui-gray-200 disabled:text-fbui-text-disabled'

type TriggerSize = 'sm' | 'md'

/** Height + gap + base text per size (`md` = the original h-9 field). */
const triggerSizeClass: Record<TriggerSize, string> = {
  sm: 'h-8 gap-1.5 text-xs',
  md: 'h-9 gap-2 text-sm',
}

/** rounded-rect (in-table default) vs fully-rounded pill; horizontal padding scales with size. */
const triggerShape: Record<'rect' | 'pill', Record<TriggerSize, string>> = {
  rect: { sm: 'rounded-lg px-2.5', md: 'rounded-lg px-[0.75rem]' },
  pill: { sm: 'rounded-full px-[0.75rem]', md: 'rounded-full px-[1rem]' },
}

/** Border-notch label offset — tracks each size+shape's horizontal padding. */
const triggerLabelLeft: Record<'rect' | 'pill', Record<TriggerSize, string>> = {
  rect: { sm: 'left-2', md: 'left-2.5' },
  pill: { sm: 'left-3', md: 'left-4' },
}

const triggerRest = {
  default:
    '!border-fbui-gray-300 text-fbui-gray-700 hover:!border-fbui-gray-400 data-[state=open]:!border-fbui-basil-600 data-[state=open]:ring-2 data-[state=open]:ring-fbui-ring-brand',
  // A value is applied — brand border at rest so the column reads as filtered.
  active:
    '!border-fbui-basil-600 text-fbui-gray-800 data-[state=open]:ring-2 data-[state=open]:ring-fbui-ring-brand',
}

export interface FilterTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: React.ReactNode
  /** Compact summary of the applied value (count or range), shown when active. */
  summary?: React.ReactNode
  /** Whether a value is currently applied — drives the brand-tinted resting look. */
  active?: boolean
  /** Leading glyph before the label (inside placement only) — e.g. a sort icon. */
  icon?: React.ReactNode
  /** Corner style: `rect` (rounded-rect, default) or `pill` (fully rounded). */
  shape?: keyof typeof triggerShape
  /** Height: `md` (h-9, default) or `sm` (h-8, 12px text) for a compact toolbar. */
  size?: TriggerSize
  /**
   * Where the label sits. `inside` (default) leads the row; `border` notches the
   * label into the top border line (frees the interior for the value / chips).
   */
  labelPlacement?: 'inside' | 'border'
  /** Interior text when `labelPlacement="border"` and nothing is applied yet. */
  placeholder?: React.ReactNode
}

/**
 * The filter pill — the `Popover` trigger (`asChild`). Two axes:
 *  - `shape`: `rect` (in-table default) or `pill` (matches saved filters).
 *  - `labelPlacement`: `inside` (label leads, applied value in a count badge) or
 *    `border` (label notched into the top border, value fills the interior — the
 *    outlined-field look that leaves room for selected chips).
 *
 * The notched label sits on a white cutout, so the `border` variant assumes a
 * white / near-white backdrop behind the pill (the usual table-header surface).
 */
export const FilterTrigger = React.forwardRef<
  HTMLButtonElement,
  FilterTriggerProps
>(
  (
    {
      className,
      label,
      summary,
      active = false,
      icon,
      shape = 'rect',
      size = 'md',
      labelPlacement = 'inside',
      placeholder = 'All',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        triggerBase,
        triggerSizeClass[size],
        triggerShape[shape][size],
        active ? triggerRest.active : triggerRest.default,
        className
      )}
      {...props}
    >
      {labelPlacement === 'border' ? (
        <>
          <span
            // translateY centers the label on the top border line; inline so it
            // never depends on a negative Tailwind utility being generated.
            style={{ transform: 'translateY(-50%)' }}
            className={cn(
              // Always gray — the green border + value signal "active", the label stays neutral.
              'pointer-events-none absolute top-0 bg-fbui-base-white px-1 text-xs font-medium leading-none text-fbui-gray-500',
              triggerLabelLeft[shape][size]
            )}
          >
            {label}
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-left',
              active ? 'text-fbui-gray-800' : 'font-normal text-fbui-gray-400'
            )}
          >
            {active && summary != null ? summary : placeholder}
          </span>
        </>
      ) : (
        <span className="flex min-w-0 items-center gap-2">
          {icon && (
            <span
              className={cn(
                'flex shrink-0 items-center text-fbui-gray-500',
                size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4'
              )}
            >
              {icon}
            </span>
          )}
          <span className="truncate">{label}</span>
          {active && summary != null && (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-fbui-basil-50 px-1.5 text-xs font-semibold text-fbui-basil-700">
              {summary}
            </span>
          )}
        </span>
      )}
      <CaretDown
        weight="bold"
        aria-hidden="true"
        className={cn(
          'shrink-0 text-fbui-gray-500 transition-transform group-data-[state=open]:rotate-180',
          size === 'sm' ? 'size-3.5' : 'size-4'
        )}
      />
    </button>
  )
)
FilterTrigger.displayName = 'FilterTrigger'

export interface FilterActionsProps {
  onReset?: () => void
  onCancel?: () => void
  onApply?: () => void
  /** Disable Reset when there's nothing staged to clear. */
  resetDisabled?: boolean
  /** Disable Apply when the draft is unchanged or invalid. */
  applyDisabled?: boolean
  resetLabel?: string
  cancelLabel?: string
  applyLabel?: string
}

/**
 * Footer row: Reset on the left, Cancel + Apply on the right. Omit `onReset` to
 * drop the Reset button (Cancel + Apply right-aligned) for value pickers where
 * "clear" isn't meaningful (e.g. a required date range).
 */
export function FilterActions({
  onReset,
  onCancel,
  onApply,
  resetDisabled,
  applyDisabled,
  resetLabel = 'Reset',
  cancelLabel = 'Cancel',
  applyLabel = 'Apply',
}: FilterActionsProps) {
  return (
    <div
      className={cn(
        // Grounded action bar: a hairline divider separates the footer from the
        // popover body (matching the DS modal / bordered-card footer), with equal
        // breathing room above and below the line.
        'mt-[0.75rem] flex items-center gap-2 border-t border-fbui-gray-200 pt-[0.75rem]',
        onReset ? 'justify-between' : 'justify-end'
      )}
    >
      {onReset && (
        <Button
          // Neutral (quiet gray hover) — reset/clear is a secondary action, not
          // a brand-positive one, so it shouldn't read as brand-green ghost.
          variant="neutral"
          size="sm"
          onClick={onReset}
          disabled={resetDisabled}
          className="-ml-1.5 px-2"
        >
          {resetLabel}
        </Button>
      )}
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          appearance="outline"
          size="sm"
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        <Button size="sm" onClick={onApply} disabled={applyDisabled}>
          {applyLabel}
        </Button>
      </div>
    </div>
  )
}

export interface FilterListboxItem {
  value: string
  label: string
  /** Optional leading glyph (e.g. a sort-direction icon). */
  leading?: React.ReactNode
}

export interface FilterListboxProps {
  items: FilterListboxItem[]
  value: string | null | undefined
  onSelect: (value: string) => void
  /** Accessible name for the listbox. */
  ariaLabel: string
  emptyText?: string
  /** Classes on the scroll container (e.g. a max-height override). */
  className?: string
}

/**
 * Accessible single-select listbox — `div[role="listbox"]` with `div[role="option"]`
 * rows (NOT `button[role="option"]`, which is an invalid role nesting). Roving
 * `tabIndex` + Arrow/Home/End/Enter/Space keyboard nav, selected row in the
 * brand-tinted DS option style with a trailing check. Shared by `SortMenu` and
 * `SavedFilterSelect`; pair it with a `SearchBar` above for a searchable list.
 */
export function FilterListbox({
  items,
  value,
  onSelect,
  ariaLabel,
  emptyText = 'No matches',
  className,
}: FilterListboxProps) {
  const refs = React.useRef<(HTMLDivElement | null)[]>([])
  const selectedIndex = items.findIndex((o) => o.value === value)
  const [activeIndex, setActiveIndex] = React.useState(
    selectedIndex >= 0 ? selectedIndex : 0
  )
  // Keep the roving tabstop in range as the list filters.
  const rove = Math.min(activeIndex, Math.max(0, items.length - 1))

  const move = (next: number) => {
    if (items.length === 0) return
    const i = (next + items.length) % items.length
    setActiveIndex(i)
    refs.current[i]?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        move(i + 1)
        break
      case 'ArrowUp':
        e.preventDefault()
        move(i - 1)
        break
      case 'Home':
        e.preventDefault()
        move(0)
        break
      case 'End':
        e.preventDefault()
        move(items.length - 1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        onSelect(items[i].value)
        break
    }
  }

  if (items.length === 0) {
    return (
      <div
        role="listbox"
        aria-label={ariaLabel}
        className={cn('fbui-scroll max-h-72 overflow-y-auto', className)}
      >
        <p className="px-[0.75rem] py-2 text-sm text-fbui-gray-500">{emptyText}</p>
      </div>
    )
  }

  return (
    <div
      role="listbox"
      aria-label={ariaLabel}
      className={cn('fbui-scroll max-h-72 overflow-y-auto', className)}
    >
      {items.map((o, i) => {
        const isActive = o.value === value
        return (
          <div
            key={o.value}
            ref={(el) => {
              refs.current[i] = el
            }}
            role="option"
            aria-selected={isActive}
            tabIndex={i === rove ? 0 : -1}
            onClick={() => onSelect(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            onFocus={() => setActiveIndex(i)}
            className={cn(
              'flex cursor-pointer items-center gap-2.5 rounded-lg px-[0.75rem] py-2 text-sm outline-none transition-colors hover:bg-fbui-gray-100 focus-visible:bg-fbui-gray-100',
              isActive
                ? 'bg-fbui-basil-50 font-medium text-fbui-basil-700'
                : 'text-fbui-gray-700'
            )}
          >
            {o.leading}
            <span className="min-w-0 flex-1 truncate">{o.label}</span>
            {isActive && (
              <Check
                weight="bold"
                aria-hidden="true"
                className="size-4 shrink-0 text-fbui-basil-700"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export interface FilterQuickItem {
  label: string
  onSelect: () => void
  /**
   * Marks this preset as the currently-applied one (brand highlight +
   * `aria-pressed`), so the choice reads as selected even when the controls
   * below don't visibly shift. Clicking a selected item can toggle it off — that
   * behavior is the consumer's (in `onSelect`).
   */
  selected?: boolean
}

export interface FilterQuickSelectProps {
  items: FilterQuickItem[]
  /** Column heading (default "Quick Select"). */
  label?: string
  className?: string
}

/**
 * Right-hand "Quick Select" column for filter popovers — a bordered list of
 * one-click presets (the date-range / period pattern). Each item fills the draft
 * via its `onSelect`; the consumer still commits on Apply.
 */
export function FilterQuickSelect({
  items,
  label = 'Quick Select',
  className,
}: FilterQuickSelectProps) {
  return (
    <div
      className={cn(
        'w-36 shrink-0 border-l border-fbui-gray-200 pl-[0.75rem]',
        className
      )}
    >
      <p className="mb-1 px-2 text-xs font-medium text-fbui-gray-500">
        {label}
      </p>
      <div className="flex flex-col">
        {items.map((it) => (
          <button
            key={it.label}
            type="button"
            aria-pressed={it.selected}
            onClick={it.onSelect}
            className={cn(
              'rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand',
              it.selected
                ? 'bg-fbui-basil-50 font-medium text-fbui-basil-700 hover:bg-fbui-basil-100'
                : 'text-fbui-gray-700 hover:bg-fbui-gray-100'
            )}
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export interface FilterDraft<T> {
  open: boolean
  onOpenChange: (next: boolean) => void
  draft: T
  setDraft: React.Dispatch<React.SetStateAction<T>>
  apply: () => void
  cancel: () => void
}

/**
 * Draft-on-apply lifecycle for a filter popover. `committed` is the live value;
 * the returned `draft` is the in-popover working copy. Opening reseeds the draft
 * from `committed` (no `useEffect` — seeded in the open handler); `apply` commits
 * via `onApply` and closes; `cancel` just closes (discarding the draft).
 */
export function useFilterDraft<T>(
  committed: T,
  onApply: (next: T) => void
): FilterDraft<T> {
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<T>(committed)
  return {
    open,
    onOpenChange: (next) => {
      if (next) setDraft(committed)
      setOpen(next)
    },
    draft,
    setDraft,
    apply: () => {
      onApply(draft)
      setOpen(false)
    },
    cancel: () => setOpen(false),
  }
}
